import type {
  ActivityShareFormat,
  ActivityShareStyle,
  CreateTripInput,
  CreateTripResponse,
  SetTripCampInput,
  SetTripCampResponse,
  SetTripRouteResponse,
  TripDetailResponse,
  TripShareData,
  TripShareUrlResponse,
  TripSuggestionsResponse,
  TripsResponse,
  UpdateTripInput,
} from "@atlas/shared";
import { ApiError, tripGpxPath, tripRouteUploadPath } from "@atlas/shared";
import { API_BASE_URL, apiClient, apiDelete, apiGet, apiPatch, apiPost, clientHeaders } from "../lib/api-client";

// Trips (spec 35 / ATL-157). Paths mirror packages/shared/src/api/trips.ts
// (canonical) and packages/api/src/routes/trips.ts.

export type {
  TripSummary,
  TripDetail,
  TripDetailResponse,
  TripMember,
  TripJoin,
  TripPeak,
  TripLandmark,
  TripTrail,
  TripRegion,
  TripSuggestion,
  TripShareData,
  TripLineString,
  TripFill,
  TripRouteInfo,
} from "@atlas/shared";

export function fetchTrips(): Promise<TripsResponse> {
  return apiGet("/trips");
}

export function createTrip(input: CreateTripInput): Promise<CreateTripResponse> {
  return apiPost("/trips", input);
}

export function fetchTrip(id: string): Promise<TripDetailResponse> {
  return apiGet(`/trips/${id}`);
}

export function updateTrip(id: string, input: UpdateTripInput): Promise<{ ok: true }> {
  return apiPatch(`/trips/${id}`, input);
}

/** Ungroups the trip; the activities are untouched. */
export function deleteTrip(id: string): Promise<{ ok: true }> {
  return apiDelete(`/trips/${id}`);
}

export function fetchTripSuggestions(opts?: { all?: boolean }): Promise<TripSuggestionsResponse> {
  return apiGet(`/trips/suggestions${opts?.all ? "?all=1" : ""}`);
}

// ─── Planned route (spec 38) ──────────────────────────────────────────────────

/** Use one of the user's activities as the planned route. Fills are computed before the answer. */
export function setTripRoute(id: string, activityId: string): Promise<SetTripRouteResponse> {
  return apiClient.put(`/trips/${id}/route`, { activityId });
}

export function deleteTripRoute(id: string): Promise<{ ok: true }> {
  return apiDelete(`/trips/${id}/route`);
}

// ─── Camps (spec 38 round 2) ──────────────────────────────────────────────────

/** Move the camp of the night after `fromActivityId`. The server snaps it and re-splits the fills. */
export function setTripCamp(id: string, fromActivityId: string, input: SetTripCampInput): Promise<SetTripCampResponse> {
  return apiClient.put(`/trips/${id}/camps/${fromActivityId}`, input);
}

/** Back to the automatic camp (GPX waypoint, else the end of that evening's recording). */
export function resetTripCamp(id: string, fromActivityId: string): Promise<{ ok: true }> {
  return apiDelete(`/trips/${id}/camps/${fromActivityId}`);
}

/** Upload a GPX straight onto the trip as its route (it never becomes an activity). */
export async function uploadTripRoute(id: string, file: File): Promise<SetTripRouteResponse> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`${API_BASE_URL}${tripRouteUploadPath(id)}`, {
    method: "POST",
    body: form,
    credentials: "include",
    headers: clientHeaders(),
  });
  const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  if (!res.ok) {
    const message = body && typeof body["error"] === "string" ? body["error"] : "Upload failed";
    throw new ApiError(res.status, message, body ?? undefined);
  }
  return body as unknown as SetTripRouteResponse;
}

export function dismissTripSuggestion(firstActivityId: string): Promise<{ ok: true }> {
  return apiPost("/trips/suggestions/dismiss", { firstActivityId });
}

export function fetchTripShareUrl(id: string, identity: boolean): Promise<TripShareUrlResponse> {
  return apiGet(`/trips/${id}/share-url${identity ? "?identity=1" : ""}`);
}

/** Loads the sig-gated public payload behind a trip share link (no session needed). */
export function fetchTripShare(id: string, sig: string, identity: boolean): Promise<TripShareData> {
  const params = new URLSearchParams({ sig });
  if (identity) params.set("i", "1");
  return apiGet(`/share/trip/${id}.json?${params}`);
}

/** Builds a share-image URL from the minted `imageUrl` base (same params as the activity share). */
export function tripShareImageUrl(
  imageUrl: string,
  opts: { format: ActivityShareFormat; style: ActivityShareStyle },
): string {
  const url = new URL(imageUrl);
  url.searchParams.set("format", opts.format);
  url.searchParams.set("style", opts.style);
  return url.toString();
}

/** Owner-only GPX download (one track per member). Uses the session cookie. */
export async function downloadTripGpx(id: string, name: string): Promise<void> {
  const res = await fetch(`${API_BASE_URL}${tripGpxPath(id)}`, {
    credentials: "include",
    headers: clientHeaders(),
  });
  if (!res.ok) throw new Error(`GPX download failed (${res.status})`);
  const blob = await res.blob();
  const slug =
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60) || "trip";
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = `${slug}.gpx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 10_000);
}
